/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DeleteOutlined, EditOutlined, PlusOutlined, SendOutlined
} from '@ant-design/icons';
import {
  Button, message, Modal, Space, Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps,
  FilterValueProps,
  mappingFilterFields,
  mappingFilterToQuery,
  mappingParamsFilter,
  mappingQueryParamsFilter,
} from 'common/components/PageTable/filter';
import useDidMount from 'common/hooks/useDidMount';
import { deleteClientService, getAllClientsService, sendMailToClientService } from 'common/services/extends/client';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateDDMMYYYY } from 'common/utils/functions';
import roles from 'configs/roles';

export const CLIENT_CONTRACT: { [key: number]: string } = {
  1: 'client.official',
  2: 'client.poc',
  3: 'client.trial',
  4: 'client.expired',
};

type ClientColumnsType = {
  id: number;
  companyName: string;
  corporateName: string;
  technicalName: string;
  technicalPhone: string;
  contractType: number;
  status: number;
  startDate: string;
  endDate: string;
};

const Client: React.FC<ActiveRoles> = ({
  roleIndex,
  roleView,
  roleCreate,
  roleDelete,
  roleUpdate,
  roleOther,
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);

  const pageParam = searchParams.get('page');

  /* Selectors */
  const {
    defaultPageSize,
    advancedFilter
  } = useAppSelector((state) => state.system);

  /* State */
  const [currentPage, setCurrentPage] = useState(Number(pageParam));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]
  >(mappingQueryParamsFilter(params));

  /* Variables */
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('clients', advancedFilter),
    [advancedFilter]
  );

  /* Queries */
  const queryKey = [
    'GetAllClients',
    keyword,
    currentPage,
    currentView,
    selectedFilterList,
  ];
  const { data: clientData, isLoading } = useQuery(
    queryKey,
    () => getAllClientsService({
      keyword,
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter,
    }),
    {
      enabled: !!currentPage,
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['DeleteClient'],
    async (ids: number[]) => deleteClientService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      },
    }
  );

  const { mutate: sendMailMutate, isLoading: sendMailLoading } = useMutation(
    ['SendMailClient'],
    sendMailToClientService,
    {
      onSuccess: () => {
        message.success(t('client.sendMailSuccess'));
      },
      onError: () => {
        message.error(t('client.sendMailError'));
      },
    }
  );

  /* Data */
  const clients: ClientColumnsType[] = useMemo(() => {
    if (clientData) {
      return clientData.data.map((item) => ({
        id: item.clientData.id,
        companyName: item.clientData.companyName,
        corporateName: item.clientData.corporateName,
        technicalName: item.clientData.technicalName,
        technicalPhone: item.clientData.technicalPhone,
        contractType: item.clientData.contractType,
        status: item.clientData.status,
        startDate: item.clientData.startDate,
        endDate: item.clientData.endDate,
      }));
    }
    return [];
  }, [clientData]);

  const columns: ColumnsType<ClientColumnsType> = [
    {
      title: 'ID',
      key: 'id',
      width: 55,
      align: 'center',
      fixed: 'left',
      render: (_name: string, data: ClientColumnsType) => (
        <Typography.Text>
          {data.id}
        </Typography.Text>
      ),
    },
    // --- Tiêu đề
    {
      title: t('client.companyName'),
      dataIndex: 'companyName',
      key: 'companyName',
      render: (_: string, data: any) => (
        <Typography.Text
          onClick={() => roleView && navigate(`${ROUTE_PATHS.CLIENT_DETAIL}?id=${data.id}`)}
          style={{ cursor: 'pointer' }}
        >
          {data.companyName}
        </Typography.Text>
      ),
    },
    {
      title: t('client.corporateName'),
      dataIndex: 'corporateName',
      key: 'corporateName',
      render: (_name: string, data: ClientColumnsType) => (
        <Typography.Text>
          {data.corporateName}
        </Typography.Text>
      ),
    },
    {
      title: t('client.technicalName'),
      dataIndex: 'technicalName',
      key: 'technicalName',
      render: (_: string, data: any) => (
        <Typography.Text>
          {data.technicalName}
        </Typography.Text>
      ),
    },
    {
      title: t('client.technicalPhone'),
      dataIndex: 'technicalPhone',
      key: 'technicalPhone',
      render: (_: string, data: any) => (
        <Typography.Text>
          {data.technicalPhone}
        </Typography.Text>
      ),
    },
    {
      title: t('client.contractType'),
      dataIndex: 'contractType',
      key: 'contractType',
      align: 'center',
      render: (_: string, data: any) => (
        <Typography.Text>
          {t(CLIENT_CONTRACT[data.contractType])}
        </Typography.Text>
      ),
    },
    {
      title: t('client.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (_: string, data: any) => (
        <Typography.Text type={data.status === 1 ? 'success' : 'warning'}>
          {data.status === 1 ? t('client.active') : t('client.inactive')}
        </Typography.Text>
      ),
    },
    {
      title: t('client.startDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      align: 'center',
      render: (_: string, data: any) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.startDate)}
        </Typography.Text>
      ),
    },
    {
      title: t('client.endDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      align: 'center',
      render: (_: string, data: any) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.endDate)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 120,
      align: 'center',
      render: (_name: string, _data: ClientColumnsType) => (
        <Space>
          {roleOther.includes(roles.CLIENT_SEND_MAIL) && (
            <Button
              loading={sendMailLoading}
              disabled={!roleOther.includes(roles.CLIENT_SEND_MAIL) || sendMailLoading}
              onClick={() => sendMailMutate(_data.id)}
              icon={<SendOutlined />}
            />
          )}
          <Button
            disabled={!roleUpdate}
            icon={<EditOutlined />}
            onClick={() => roleIndex && navigate(`${ROUTE_PATHS.CLIENT_DETAIL}?id=${_data.id}`)}
          />
          <Button
            disabled={!roleDelete || deleteLoading}
            loading={deleteLoading}
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                className: 't-pagetable_deleteRecordModal',
                autoFocusButton: 'cancel',
                title: t('message.confirmDeleteRecord'),
                okText: t('system.ok'),
                cancelText: t('system.cancel'),
                cancelButtonProps: {
                  type: 'primary',
                },
                okButtonProps: {
                  type: 'default',
                },
                onOk: () => {
                  deleteMutate([_data.id]);
                },
              });
            }}
          />
        </Space>
      ),
    },
  ];

  /* Functions */

  // Change page view
  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() }, { replace: true });
  };

  // Change page size
  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  // Add filter
  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if (
      (typeFilter === 'isNull' || typeFilter === 'isNotNull')
      && selectedFilterList.find((item) => item.key === data.key)
    ) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
  };

  // Delete filter
  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(
      tempList.filter((item) => !(item.key === key && item.index === index))
    );
  };

  /* Effects */
  useEffect(() => {
    setCurrentPage(1);
    setSearchParams(
      {
        ...mappingFilterToQuery(selectedFilterList),
        page: '1',
      },
      { replace: true }
    );
  }, [keyword, setSearchParams, selectedFilterList]);

  useDidMount(() => {
    if (pageParam) return setCurrentPage(Number(pageParam));
    return setCurrentPage(1);
  });

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.client')}
        rightHeader={(
          <Button
            type="primary"
            disabled={!roleCreate}
            onClick={() => {
              navigate(`${ROUTE_PATHS.CLIENT_DETAIL}`);
            }}
          >
            <PlusOutlined />
            {t('system.create')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={() => { }}
          isLoading={isLoading}
          handleEditPage={(id) => {
            navigate(`${ROUTE_PATHS.CLIENT_DETAIL}?id=${id}`);
          }}
          handleCreatePage={(id) => {
            navigate(`${ROUTE_PATHS.CLIENT_DETAIL}?id=${id}`);
          }}
          handleSearch={setKeyword}
          roles={{ roleCreate, roleDelete, roleUpdate }}
          tableProps={{
            initShowColumns: ['id', 'companyName', 'corporateName', 'technicalName', 'technicalPhone', 'contractType', 'status', 'startDate', 'endDate', 'action'],
            columns,
            pageData: clients,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: clientData?.meta.total || 0,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields,
          }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
        />
      </div>
    </>
  );
};

export default Client;
